import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SuperAdminDashboard.css";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    rooms: 0,
    bookings: 0,
    blockedSlots: 0,
    inquiries: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const adminUser = localStorage.getItem("superAdminUser") || "Super Admin";

  useEffect(() => {
    if (localStorage.getItem("superAdminAuth") !== "true") {
      navigate("/superadmin/login", { replace: true });
      return;
    }
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const [rRes, bRes, iRes] = await Promise.all([
        fetch(`${API_BASE}/rooms`).then((r) =>
          r.ok ? r.json() : Promise.reject("Rooms fetch failed")
        ),
        fetch(`${API_BASE}/bookings/admin/all`).then((r) =>
          r.ok ? r.json() : Promise.reject("Bookings fetch failed")
        ),
        fetch(`${API_BASE}/inquiries`).then((r) =>
          r.ok ? r.json() : Promise.reject("Inquiries fetch failed")
        ),
      ]);

      const rooms = rRes?.data?.length || 0;
      const bookings = bRes?.data?.length || 0;
      const blockedSlots = (bRes?.data || []).filter(
        (b) => b.state === "COMPLETED" && b.meta?.adminBlocked
      ).length;
      const inquiries = iRes?.data?.length || 0;

      setStats({ rooms, bookings, blockedSlots, inquiries });
      setLastUpdated(new Date());
    } catch (e) {
      console.error("Failed to load stats:", e);
      setError("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("superAdminAuth");
      localStorage.removeItem("superAdminUser");
      navigate("/superadmin/login", { replace: true });
    }
  };

  const formatTime = (date) => {
    if (!date) return "";
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const tools = [
    {
      icon: "🎭",
      title: "RoomCard Slot Control",
      description:
        "Enable or disable any time slot for any theater on any date with precision control",
      path: "/superadmin/roomcard-control",
      cta: "Open Slot Control",
      gradient: "linear-gradient(135deg, #6366f1, #4f46e5)",
    },
    {
      icon: "📊",
      title: "View All Database",
      description:
        "Centralized view of all customer inquiries, bookings, events & gaming sessions",
      path: "/superadmin/customer-db",
      cta: "Open Database",
      gradient: "linear-gradient(135deg, #ec4899, #db2777)",
    },
    {
      icon: "❄️",
      title: "Frozen Database",
      description:
        "View and unlock frozen bookings from the admin panel with audit logs",
      path: "/SuperAdminFrozenOrders",
      cta: "Open Frozen DB",
      gradient: "linear-gradient(135deg, #10b981, #059669)",
    },
    {
      icon: "📦",
      title: "Stock Management",
      description:
        "Real-time inventory tracking for cakes, decorations, gifts & venue services",
      path: "/superadmin/stock-management",
      cta: "Open Stock Management",
      gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
    },
    {
      icon: "🛠️",
      title: "Admin Panel",
      description:
        "Configure add-ons, gifts, cakes and service offerings with pricing",
      path: "/AdminPanel",
      cta: "Open Admin Panel",
      gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
    },
    {
      icon: "🎟️",
      title: "RoomCard Management",
      description:
        "Edit & Update Room Cards, theater configurations and seating layouts",
      path: "/AdminPanelRoomCard",
      cta: "Open RoomCard Editor",
      gradient: "linear-gradient(135deg, #06b6d4, #0891b2)",
    },
    {
      icon: "👥",
      title: "Staff Management",
      description:
        "Manage staff accounts, roles, permissions and system access levels",
      path: "/StaffManagement",
      cta: "Manage Staff",
      gradient: "linear-gradient(135deg, #f97316, #ea580c)",
    },
    {
      icon: "📦",
      title: "Auditor Invoice Generation",
      description: "Real-time Invoice tracking",
      path: "/InvoiceMaker",
      cta: "Invoice Management",
      gradient: "linear-gradient(135deg, #76C8E8, #8576E8)",
    },
    {
      icon: "🔄",
      title: "Transfer Database",
      description:
        "Securely transfer and migrate contact data between storage systems",
      path: "/TransferContact",
      cta: "Transfer Data",
      gradient: "linear-gradient(135deg, #64748b, #475569)",
    },
    {
      icon: "➕",
      title: "Extra Addons",
      description:
        "Manage additional addons and services available for your customers. Configure extra options and enhance your booking experience.",
      path: "https://www.thehexaplay.com/Extraaddon",
      cta: "Open Extra Addons",
      gradient: "linear-gradient(135deg, #14b8a6, #0f766e)",
      external: true,
    },
  ];

  const handleToolNavigation = (tool) => {
    if (tool.external) {
      window.location.href = tool.path;
    } else {
      navigate(tool.path);
    }
  };

  return (
    <div className="sa-dashboard">
      <header className="sa-header">
        <div className="sa-header-inner">
          <div className="sa-header-left">
            <div className="sa-logo">🛡️</div>
            <div className="sa-header-brand">
              <h1>Super Admin Panel</h1>
              <p>HexaPlay Control Center</p>
            </div>
          </div>

          <div className="sa-header-right">
            <div className="sa-user-pill">
              <span className="sa-user-avatar">👤</span>
              <div className="sa-user-info">
                <span className="sa-user-name">{adminUser}</span>
                <span className="sa-user-role">Super Administrator</span>
              </div>
            </div>

            <button className="sa-logout-btn" onClick={handleLogout}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="sa-main">
        <div className="sa-welcome">
          <div className="sa-welcome-text">
            <h2>
              Welcome back, <span>{adminUser}</span>
            </h2>
            <p>
              Monitor system performance and manage operations from your
              centralized dashboard.
            </p>
          </div>
        </div>

        {error && (
          <div className="sa-error-banner">
            <span>⚠️ {error}</span>
            <button onClick={fetchStats}>Retry</button>
          </div>
        )}

        <div className="sa-stats-header">
          <h3 className="sa-section-title">Overview</h3>

          <div className="sa-stats-meta">
            {lastUpdated && (
              <span className="sa-last-updated">
                Last updated: {formatTime(lastUpdated)}
              </span>
            )}

            <button
              className="sa-refresh-btn"
              onClick={fetchStats}
              disabled={loading}
              title="Refresh Statistics"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={loading ? "spin" : ""}
              >
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            </button>
          </div>
        </div>

        <div className="sa-stats">
          <StatCard
            icon="🎬"
            label="Total Theaters"
            value={loading ? null : stats.rooms}
            color="#6366f1"
            loading={loading}
          />

          <StatCard
            icon="📅"
            label="Total Bookings"
            value={loading ? null : stats.bookings}
            color="#ec4899"
            loading={loading}
          />

          <StatCard
            icon="📞"
            label="Total Inquiries"
            value={loading ? null : stats.inquiries}
            color="#10b981"
            loading={loading}
          />

          <StatCard
            icon="🔒"
            label="Blocked Slots"
            value={loading ? null : stats.blockedSlots}
            color="#f59e0b"
            loading={loading}
          />
        </div>

        <h3 className="sa-section-title">Management Tools</h3>

        <div className="sa-tools-grid">
          {tools.map((tool, i) => (
            <div
              key={i}
              className="sa-tool-card"
              style={{
                animationDelay: `${i * 0.06}s`,
                "--card-color": tool.gradient,
              }}
              onClick={() => handleToolNavigation(tool)}
            >
              <div
                className="sa-tool-icon"
                style={{ background: tool.gradient }}
              >
                {tool.icon}
              </div>

              <div className="sa-tool-content">
                <h4 className="sa-tool-title">{tool.title}</h4>
                <p className="sa-tool-desc">{tool.description}</p>
              </div>

              <button
                className="sa-tool-btn"
                style={{ background: tool.gradient }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToolNavigation(tool);
                }}
              >
                {tool.cta}

                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ icon, label, value, color, loading }) => (
  <div className="sa-stat" style={{ "--stat-color": color }}>
    <div
      className="sa-stat-icon"
      style={{ background: `${color}15`, color }}
    >
      {icon}
    </div>

    <div className="sa-stat-info">
      <div className="sa-stat-label">{label}</div>

      <div className="sa-stat-value">
        {loading ? (
          <span className="sa-skeleton-loader"></span>
        ) : (
          value?.toLocaleString() || "0"
        )}
      </div>
    </div>
  </div>
);

export default SuperAdminDashboard;